import React, { useState, useEffect } from "react";
import "./EditStep.css";

const EditStep = ({
  csvData,
  validationErrors,
  handleUpdateCsvData,
  uploadType,
}) => {
  const [editedData, setEditedData] = useState([]);
  const [errors, setErrors] = useState([]);

  useEffect(() => {
    setEditedData(csvData);
    setErrors(validationErrors);
  }, [csvData, validationErrors]);

  const totalErrorRows = errors.filter(
    (error) => error && error.errors.length > 0
  ).length;

  const getExpectedFormat = () => {
    switch (uploadType) {
      case "eventCreate":
        return (
          <div className="format-guide">
            <p>Expected CSV format for Event Creation:</p>
            <ul>
              <li>event_name: SCORE, HOTS, Math Olympiad, or I-EMSAT</li>
              <li>start_date: dd-mm-yyyy</li>
              <li>end_date: dd-mm-yyyy</li>
              <li>start_time: hh:mm AM/PM</li>
              <li>end_time: hh:mm AM/PM</li>
              <li>event_level: Prelims, Semi-Finals, or Finals</li>
              <li>event_round: Round X (where X is a number)</li>
              <li>school_id: Valid school ID format</li>
            </ul>
          </div>
        );
      case "eventReschedule":
        return (
          <div className="format-guide">
            <p>Expected CSV format for Event Rescheduling:</p>
            <ul>
              <li>event_name: Valid event name (e.g., SCORE, HOTS)</li>
              <li>school_id: Valid school ID format</li>
              <li>start_date_time: YYYY-MM-DDTHH:mm</li>
              <li>end_date_time: YYYY-MM-DDTHH:mm</li>
              <li>level: Prelims, Semi-Finals, or Finals</li>
              <li>round: Positive integer (e.g., 1, 2, 3)</li>
            </ul>
          </div>
        );
      case "eventDelete":
        return (
          <div className="format-guide">
            <p>Expected CSV format for Event Deletion:</p>
            <ul>
              <li>school_id: Valid school ID format</li>
              <li>start_date: dd-mm-yyyy</li>
              <li>end_date: dd-mm-yyyy</li>
              <li>start_time: hh:mm AM/PM</li>
              <li>end_time: hh:mm AM/PM</li>
              <li>level: Event level</li>
              <li>round: Event round</li>
            </ul>
          </div>
        );
      case "studentRegister":
        return (
          <div className="format-guide">
            <p>Expected CSV format for Student Registration:</p>
            <ul>
              <li>isd_code: Valid ISD code</li>
              <li>first_name: Letters only</li>
              <li>last_name: Letters only (optional)</li>
              <li>
                phone: For India (ISD 91): 10 digits, For UAE (ISD 971): 7
                digits starting with 5 or higher
              </li>
              <li>grade: 1-21</li>
              <li>school_id: Valid school ID format</li>
            </ul>
          </div>
        );
      case "uploadOfflineResponses":
        return (
          <div className="format-guide">
            <p>Expected CSV format for Offline Responses:</p>
            <ul>
              <li>first_name: Letters only</li>
              <li>last_name: Letters only (optional)</li>
              <li>isd_code: Valid ISD code</li>
              <li>
                phone: For India (ISD 91): 10 digits, For UAE (ISD 971): 9
                digits starting with 5 or higher
              </li>
              <li>grade: 1-21</li>
              <li>school_id: Valid school ID format</li>
              <li>
                admission_id: Alphanumeric, 6-9 characters (must be unique
                across all rows)
              </li>
              <li>
                Q1, Q2, Q3, Q4,...: Answer values (must be 1, 2, 3, or 4 only)
              </li>
              <li>login_id: Must match admission_id</li>
              <li>exam_name: Name of the exam (e.g., NEET)</li>
            </ul>
          </div>
        );
      default:
        return null;
    }
  };

  const isIncorrectFormat = errors.some((error) => {
    const expectedKeys =
      {
        eventCreate: [
          "event_name",
          "start_date",
          "end_date",
          "start_time",
          "end_time",
          "event_level",
          "event_round",
          "school_id",
        ],
        eventReschedule: [
          "event_name",
          "school_id",
          "start_date_time",
          "end_date_time",
          "level",
          "round",
        ],
        eventDelete: [
          "school_id",
          "start_date",
          "end_date",
          "start_time",
          "end_time",
          "level",
          "round",
        ],
        studentRegister: [
          "isd_code",
          "first_name",
          "last_name",
          "phone",
          "grade",
          "school_id",
        ],
        uploadOfflineResponses: [
          "first_name",
          "last_name",
          "isd_code",
          "phone",
          "grade",
          "school_id",
          "admission_id",
          "login_id",
          "exam_name",
        ],
      }[uploadType] || [];

    return !error.data || !expectedKeys.every((key) => key in error.data);
  });

  return (
    <div className="editcontianer">
      <h3>Total Error Rows: {totalErrorRows}</h3>

      {isIncorrectFormat && (
        <div className="format-error">
          <h4>⚠️ Incorrect CSV Format Detected</h4>
          <p>
            The uploaded CSV does not match the expected format for{" "}
            {uploadType.replace(/([A-Z])/g, " $1").toLowerCase()}.
          </p>
          {getExpectedFormat()}
          <div className="uploaded-data">
            <p>Your uploaded data:</p>
            <pre>{JSON.stringify(errors[0]?.data || {}, null, 2)}</pre>
          </div>
        </div>
      )}

      {!isIncorrectFormat && (
        <table>
          <thead>
            <tr>
              {uploadType === "eventCreate" && (
                <>
                  <th>Event Name</th>
                  <th>Start Date</th>
                  <th>End Date</th>
                  <th>Start Time</th>
                  <th>End Time</th>
                  <th>Event Level</th>
                  <th>Event Round</th>
                </>
              )}
              {uploadType === "eventReschedule" && (
                <>
                  <th>School ID</th>
                  <th>Event Name</th>
                  <th>Start Date Time</th>
                  <th>End Date Time</th>
                  <th>Level</th>
                  <th>Round</th>
                </>
              )}
              {uploadType === "eventDelete" && (
                <>
                  <th>Start Date</th>
                  <th>End Date</th>
                  <th>Start Time</th>
                  <th>End Time</th>
                  <th>Level</th>
                  <th>Round</th>
                </>
              )}
              {uploadType === "studentRegister" && (
                <>
                  <th>ISD Code</th>
                  <th>First Name</th>
                  <th>Last Name</th>
                  <th>Phone</th>
                  <th>Grade</th>
                  <th>School ID</th>
                </>
              )}
              {uploadType === "uploadOfflineResponses" && (
                <>
                  <th>First Name</th>
                  <th>Last Name</th>
                  <th>ISD Code</th>
                  <th>Phone</th>
                  <th>Grade</th>
                  <th>School ID</th>
                  <th>Admission ID</th>
                  <th>Login ID</th>
                  <th>Exam Name</th>
                  {errors[0]?.data &&
                    Object.keys(errors[0].data)
                      .filter((key) => key.match(/^Q\d+$/))
                      .sort((a, b) => {
                        const aNum = parseInt(a.slice(1));
                        const bNum = parseInt(b.slice(1));
                        return aNum - bNum;
                      })
                      .map((key) => <th key={key}>{key}</th>)}
                </>
              )}
            </tr>
          </thead>
          <tbody>
            {errors.map((error, index) => (
              <tr className="trcontainer" key={index}>
                {uploadType === "eventCreate" && (
                  <>
                    <td
                      className={
                        error.errors.some((err) =>
                          err.toLowerCase().includes("event name")
                        )
                          ? "errorMessage"
                          : ""
                      }
                    >
                      {error?.data?.event_name}
                    </td>
                    <td
                      className={
                        error.errors.some((err) =>
                          err.toLowerCase().includes("start date")
                        )
                          ? "errorMessage"
                          : ""
                      }
                    >
                      {error?.data?.start_date}
                    </td>
                    <td
                      className={
                        error.errors.some((err) =>
                          err.toLowerCase().includes("end date")
                        )
                          ? "errorMessage"
                          : ""
                      }
                    >
                      {error?.data?.end_date}
                    </td>
                    <td
                      className={
                        error.errors.some((err) =>
                          err.toLowerCase().includes("start time")
                        )
                          ? "errorMessage"
                          : ""
                      }
                    >
                      {error?.data?.start_time}
                    </td>
                    <td
                      className={
                        error.errors.some((err) =>
                          err.toLowerCase().includes("end time")
                        )
                          ? "errorMessage"
                          : ""
                      }
                    >
                      {error?.data?.end_time}
                    </td>
                    <td
                      className={
                        error.errors.some((err) =>
                          err.toLowerCase().includes("event level")
                        )
                          ? "errorMessage"
                          : ""
                      }
                    >
                      {error?.data?.event_level}
                    </td>
                    <td
                      className={
                        error.errors.some((err) =>
                          err.toLowerCase().includes("event round")
                        )
                          ? "errorMessage"
                          : ""
                      }
                    >
                      {error?.data?.event_round}
                    </td>
                  </>
                )}
                {uploadType === "eventReschedule" && (
                  <>
                    <td
                      className={
                        error.errors.some((err) =>
                          err.toLowerCase().includes("school id")
                        )
                          ? "errorMessage"
                          : ""
                      }
                    >
                      {error?.data?.school_id}
                    </td>
                    <td
                      className={
                        error.errors.some((err) =>
                          err.toLowerCase().includes("event name")
                        )
                          ? "errorMessage"
                          : ""
                      }
                    >
                      {error?.data?.event_name}
                    </td>
                    <td
                      className={
                        error.errors.some((err) =>
                          err.toLowerCase().includes("start date time")
                        )
                          ? "errorMessage"
                          : ""
                      }
                    >
                      {error?.data?.start_date_time}
                    </td>
                    <td
                      className={
                        error.errors.some((err) =>
                          err.toLowerCase().includes("end date time")
                        )
                          ? "errorMessage"
                          : ""
                      }
                    >
                      {error?.data?.end_date_time}
                    </td>
                    <td
                      className={
                        error.errors.some((err) =>
                          err.toLowerCase().includes("level")
                        )
                          ? "errorMessage"
                          : ""
                      }
                    >
                      {error?.data?.level}
                    </td>
                    <td
                      className={
                        error.errors.some((err) =>
                          err.toLowerCase().includes("round")
                        )
                          ? "errorMessage"
                          : ""
                      }
                    >
                      {error?.data?.round}
                    </td>
                  </>
                )}
                {uploadType === "eventDelete" && (
                  <>
                    <td
                      className={
                        error.errors.some((err) =>
                          err.toLowerCase().includes("start date")
                        )
                          ? "errorMessage"
                          : ""
                      }
                    >
                      {error?.data?.start_date}
                    </td>
                    <td
                      className={
                        error.errors.some((err) =>
                          err.toLowerCase().includes("end date")
                        )
                          ? "errorMessage"
                          : ""
                      }
                    >
                      {error?.data?.end_date}
                    </td>
                    <td
                      className={
                        error.errors.some((err) =>
                          err.toLowerCase().includes("start time")
                        )
                          ? "errorMessage"
                          : ""
                      }
                    >
                      {error?.data?.start_time}
                    </td>
                    <td
                      className={
                        error.errors.some((err) =>
                          err.toLowerCase().includes("end time")
                        )
                          ? "errorMessage"
                          : ""
                      }
                    >
                      {error?.data?.end_time}
                    </td>
                    <td
                      className={
                        error.errors.some((err) =>
                          err.toLowerCase().includes("level")
                        )
                          ? "errorMessage"
                          : ""
                      }
                    >
                      {error?.data?.level}
                    </td>
                    <td
                      className={
                        error.errors.some((err) =>
                          err.toLowerCase().includes("round")
                        )
                          ? "errorMessage"
                          : ""
                      }
                    >
                      {error?.data?.round}
                    </td>
                  </>
                )}
                {uploadType === "studentRegister" && (
                  <>
                    <td
                      className={
                        error.errors.some((err) =>
                          err.toLowerCase().includes("isd code")
                        )
                          ? "errorMessage"
                          : ""
                      }
                    >
                      {error?.data?.isd_code}
                    </td>
                    <td
                      className={
                        error.errors.some((err) =>
                          err.toLowerCase().includes("first name")
                        )
                          ? "errorMessage"
                          : ""
                      }
                    >
                      {error?.data?.first_name}
                    </td>
                    <td
                      className={
                        error.errors.some((err) =>
                          err.toLowerCase().includes("last name")
                        )
                          ? "errorMessage"
                          : ""
                      }
                    >
                      {error?.data?.last_name}
                    </td>
                    <td
                      className={
                        error.errors.some((err) =>
                          err.toLowerCase().includes("phone")
                        )
                          ? "errorMessage"
                          : ""
                      }
                    >
                      {error?.data?.phone}
                    </td>
                    <td
                      className={
                        error.errors.some((err) =>
                          err.toLowerCase().includes("grade")
                        )
                          ? "errorMessage"
                          : ""
                      }
                    >
                      {error?.data?.grade}
                    </td>
                    <td
                      className={
                        error.errors.some((err) =>
                          err.toLowerCase().includes("school")
                        )
                          ? "errorMessage"
                          : ""
                      }
                    >
                      {error?.data?.school_id}
                    </td>
                  </>
                )}
                {uploadType === "uploadOfflineResponses" && (
                  <>
                    <td
                      className={
                        error.errors.some((err) =>
                          err.toLowerCase().includes("first name")
                        )
                          ? "errorMessage"
                          : ""
                      }
                    >
                      {error?.data?.first_name}
                    </td>
                    <td
                      className={
                        error.errors.some((err) =>
                          err.toLowerCase().includes("last name")
                        )
                          ? "errorMessage"
                          : ""
                      }
                    >
                      {error?.data?.last_name}
                    </td>
                    <td
                      className={
                        error.errors.some((err) =>
                          err.toLowerCase().includes("isd code")
                        )
                          ? "errorMessage"
                          : ""
                      }
                    >
                      {error?.data?.isd_code}
                    </td>
                    <td
                      className={
                        error.errors.some((err) =>
                          err.toLowerCase().includes("phone")
                        )
                          ? "errorMessage"
                          : ""
                      }
                    >
                      {error?.data?.phone}
                    </td>
                    <td
                      className={
                        error.errors.some((err) =>
                          err.toLowerCase().includes("grade")
                        )
                          ? "errorMessage"
                          : ""
                      }
                    >
                      {error?.data?.grade}
                    </td>
                    <td
                      className={
                        error.errors.some((err) =>
                          err.toLowerCase().includes("school")
                        )
                          ? "errorMessage"
                          : ""
                      }
                    >
                      {error?.data?.school_id}
                    </td>
                    <td
                      className={
                        error.errors.some((err) =>
                          err.toLowerCase().includes("admission")
                        )
                          ? "errorMessage"
                          : ""
                      }
                    >
                      {error?.data?.admission_id}
                    </td>
                    <td
                      className={
                        error.errors.some((err) =>
                          err.toLowerCase().includes("login")
                        )
                          ? "errorMessage"
                          : ""
                      }
                    >
                      {error?.data?.login_id}
                    </td>
                    <td
                      className={
                        error.errors.some((err) =>
                          err.toLowerCase().includes("exam")
                        )
                          ? "errorMessage"
                          : ""
                      }
                    >
                      {error?.data?.exam_name}
                    </td>
                    {Object.keys(error?.data || {})
                      .filter((key) => key.match(/^Q\d+$/))
                      .sort((a, b) => {
                        const aNum = parseInt(a.slice(1));
                        const bNum = parseInt(b.slice(1));
                        return aNum - bNum;
                      })
                      .map((key) => (
                        <td
                          key={key}
                          className={
                            error.errors.some((err) =>
                              err.toLowerCase().includes(key.toLowerCase())
                            )
                              ? "errorMessage"
                              : ""
                          }
                        >
                          {error?.data[key]}
                        </td>
                      ))}
                  </>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
};

export default EditStep;
